import React, { useState, useEffect } from "react";
import {
  Card,
  CardHeader,
  CardContent,
  Divider,
  Snackbar,
  Alert,
  Box,
  Fade,

  TextField,
  Button,
  Typography,
  Switch,
  Grid,
  Autocomplete
} from "@mui/material";
import { makeRequest } from "../../../../assets/functions_helper";
import SettingItem from "./settingItem";
import ConfirmDialog from "./confirmDialog";
import { useNavigate } from "react-router-dom";

// ── Helpers de conversión de hora ──────────────────────────────────────────
// Recibe "HH:MM" (en una zona horaria de origen) y devuelve "HH:MM" en la zona destino.
const convertTime = (timeStr, fromTz, toTz) => {
  if (!timeStr || timeStr.length < 5) return timeStr;
  const [h, m] = timeStr.split(':').map(Number);
  // Usamos una fecha fija (sin DST ambiguo) para calcular el offset.
  const refDate = new Date(`2000-01-15T${String(h).padStart(2,'0')}:${String(m).padStart(2,'0')}:00`);

  const toLocal = (tz) => {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: tz,
      hour: '2-digit', minute: '2-digit', hour12: false
    }).formatToParts(refDate);
    const hour   = parseInt(parts.find(p => p.type === 'hour').value, 10);
    const minute = parseInt(parts.find(p => p.type === 'minute').value, 10);
    return hour * 60 + minute; // minutos desde medianoche
  };

  const srcMinutes = toLocal(fromTz);
  const dstMinutes = toLocal(toTz);
  const diffMinutes = dstMinutes - srcMinutes;  // cuántos minutos hay que sumar
  const totalOriginal = h * 60 + m;
  let result = (totalOriginal + diffMinutes + 1440) % 1440; // mantener en [0, 1440)
  const rh = Math.floor(result / 60);
  const rm = result % 60;
  return `${String(rh).padStart(2,'0')}:${String(rm).padStart(2,'0')}`;
};

// UTC → hora local del restaurante  (para mostrar al admin)
const utcToLocal = (timeStr, localTz) => convertTime(timeStr, 'UTC', localTz);
// Hora local del restaurante → UTC  (para enviar al backend)
const localToUtc = (timeStr, localTz) => convertTime(timeStr, localTz, 'UTC');

const RestaurantSettings = ({ restaurantId }) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const [confirmData, setConfirmData] = useState(null);

  // Estados de configuración general
  const [requireGeolocation, setRequireGeolocation] = useState(false);
  const [isCodeEnabled, setIsCodeEnabled] = useState(false);
  const [codeDay, setCodeDay] = useState(null);

  // Estado para WhatsApp y Dirección
  const [whatsappPhoneInput, setWhatsappPhoneInput] = useState("");
  const [addressInput, setAddressInput] = useState("");

  // Estado para Google Analytics
  const [googleAnalyticsId, setGoogleAnalyticsId] = useState("");

  // Constantes de horarios
  const daysOfWeek = [
    { key: "monday", label: "Lunes" },
    { key: "tuesday", label: "Martes" },
    { key: "wednesday", label: "Miércoles" },
    { key: "thursday", label: "Jueves" },
    { key: "friday", label: "Viernes" },
    { key: "saturday", label: "Sábado" },
    { key: "sunday", label: "Domingo" },
  ];

  const defaultHours = daysOfWeek.reduce((acc, day) => {
    acc[day.key] = { closed: true, open: "08:00", close: "20:00" };
    return acc;
  }, {});

  // Estado de Horarios y Zona Horaria
  const [operatingHours, setOperatingHours] = useState(defaultHours);
  const [timezone, setTimezone] = useState(Intl.DateTimeFormat().resolvedOptions().timeZone);

  const navigate = useNavigate();

  // --- Cargar configuración inicial ---
  useEffect(() => {
    const fetchSettings = async () => {
      try {
        setLoading(true);

        // 1️⃣ Geolocalización
        const geoPromise = makeRequest(
          "GET",
          `restaurant_admin/restaurant_location/${restaurantId}`,
          null,
          "application/json",
          localStorage.getItem("token"),
          localStorage.getItem("tokenType"),
          null,
          null,
          () => navigate("/login")
        );

        // 2️⃣ Código restaurante
        const codePromise = makeRequest(
          "GET",
          `restaurant_admin/restaurant_code/${restaurantId}`,
          null,
          "application/json",
          localStorage.getItem("token"),
          localStorage.getItem("tokenType"),
          null,
          null,
          () => navigate("/login")
        );

        // 3️⃣ WhatsApp
        const waPromise = makeRequest(
          "GET",
          `restaurant_admin/whatsapp_phone/${restaurantId}`,
          null,
          "application/json",
          localStorage.getItem("token"),
          localStorage.getItem("tokenType")
        );

        // 4️⃣ Google Analytics
        const gaPromise = makeRequest(
          "GET",
          `restaurant_admin/google_analytics/${restaurantId}`,
          null,
          "application/json",
          localStorage.getItem("token"),
          localStorage.getItem("tokenType")
        );

        // 5️⃣ Operating Hours
        const ohPromise = makeRequest(
          "GET",
          `reservations/operating-hours?restaurant_public_id=${localStorage.getItem('restaurant_public_id') || restaurantId}`,
          null,
          "application/json",
          localStorage.getItem("token"),
          localStorage.getItem("tokenType")
        );

        const results = await Promise.allSettled([
          geoPromise, codePromise, waPromise, gaPromise, ohPromise
        ]);

        const [geoResult, codeResult, waResult, gaResult, ohResult] = results;

        let waPhone = "";
        if (waResult.status === "fulfilled" && waResult.value) {
          waPhone = waResult.value.whatsapp_phone || "";
          if (waResult.value.address) setAddressInput(waResult.value.address);
        }

        if (gaResult.status === "fulfilled" && gaResult.value && gaResult.value.google_analytics_measurement_id) {
          setGoogleAnalyticsId(gaResult.value.google_analytics_measurement_id);
        }

        if (ohResult.status === "fulfilled" && ohResult.value) {
          const ohResp = ohResult.value;
          const tz = ohResp.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone;
          if (ohResp.timezone) setTimezone(ohResp.timezone);
          if (ohResp.operating_hours) {
            const mergedHours = { ...defaultHours };
            for (const dayKey of Object.keys(ohResp.operating_hours)) {
              if (mergedHours[dayKey] !== undefined) {
                const raw = ohResp.operating_hours[dayKey];
                mergedHours[dayKey] = {
                  ...raw,
                  open:  raw.closed ? raw.open  : utcToLocal(raw.open?.substring(0,5),  tz),
                  close: raw.closed ? raw.close : utcToLocal(raw.close?.substring(0,5), tz),
                };
              }
            }
            setOperatingHours(mergedHours);
          }
        }

        const geoResp = geoResult.status === "fulfilled" ? geoResult.value : null;
        const codeResp = codeResult.status === "fulfilled" ? codeResult.value : null;

        // Set de valores iniciales
        setRequireGeolocation(geoResp?.require_geolocation ?? false);
        setIsCodeEnabled(codeResp?.required_code ?? false);
        setCodeDay(codeResp?.code ?? "No asignado");
        setWhatsappPhoneInput(waPhone);
      } catch (err) {
        console.error(err);
        setSnackbar({
          open: true,
          message: "Error general al obtener configuraciones del restaurante.",
          severity: "error",
        });
      } finally {
        setLoading(false);
      }
    };

    fetchSettings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [restaurantId, navigate]);

  // --- Actualizar Geolocalización ---
  const updateGeolocation = async (value) => {
    setSaving(true);
    try {
      await makeRequest(
        "PUT",
        `restaurant_admin/restaurant_location/${restaurantId}`,
        { longitude: 0, latitude: 0, require_geolocation: value },
        "application/json",
        localStorage.getItem("token"),
        localStorage.getItem("tokenType"),
        null,
        null,
        () => navigate("/login")
      );

      setRequireGeolocation(value);
      setSnackbar({
        open: true,
        message: value ? "Geolocalización activada correctamente." : "Geolocalización desactivada.",
        severity: "success",
      });
    } catch (err) {
      setSnackbar({ open: true, message: "Error al actualizar la geolocalización.", severity: "error" });
    } finally {
      setSaving(false);
    }
  };

  // --- Actualizar Código restaurante ---
  const updateCodeStatus = async (value) => {
    setSaving(true);
    try {
      let response = await makeRequest(
        "PUT",
        `restaurant_admin/code/${restaurantId}?require_code=${value}`,
        null,
        "application/json",
        localStorage.getItem("token"),
        localStorage.getItem("tokenType"),
        null,
        null,
        () => navigate("/login")
      );

      setIsCodeEnabled(value);
      if (value === true) {
        setCodeDay(response?.restaurant_new_code ?? "No asignado");
      }
      setSnackbar({
        open: true,
        message: value ? "Código del restaurante activado correctamente." : "Código desactivado.",
        severity: "success",
      });
    } catch (err) {
      setSnackbar({ open: true, message: "Error al actualizar el código del restaurante.", severity: "error" });
    } finally {
      setSaving(false);
    }
  };

  // --- Actualizar WhatsApp ---
  const handleUpdateWhatsapp = async () => {
    if (!whatsappPhoneInput.trim() && !addressInput.trim()) {
      setSnackbar({ open: true, message: "Debe suministrar al menos una información.", severity: "error" });
      return;
    }
    setSaving(true);
    try {
      await makeRequest(
        "PUT",
        `restaurant_admin/whatsapp_phone/${restaurantId}`,
        { 
          whatsapp_phone: whatsappPhoneInput.trim() || null, 
          address: addressInput.trim() || null 
        },
        "application/json",
        localStorage.getItem("token"),
        localStorage.getItem("tokenType")
      );
      setSnackbar({ open: true, message: "Datos de contacto actualizados con éxito.", severity: "success" });
    } catch (err) {
      setSnackbar({ open: true, message: "Error al actualizar la información de contacto.", severity: "error" });
    } finally {
      setSaving(false);
    }
  };

  // --- Actualizar Google Analytics ---
  const handleUpdateAnalytics = async () => {
    setSaving(true);
    try {
      await makeRequest(
        "PUT",
        `restaurant_admin/google_analytics/${restaurantId}`,
        { 
          google_analytics_measurement_id: googleAnalyticsId.trim() || null 
        },
        "application/json",
        localStorage.getItem("token"),
        localStorage.getItem("tokenType")
      );
      setSnackbar({ open: true, message: "Google Analytics actualizado con éxito.", severity: "success" });
    } catch (err) {
      setSnackbar({ open: true, message: "Error al actualizar Google Analytics.", severity: "error" });
    } finally {
      setSaving(false);
    }
  };

  // --- Actualizar Horarios de Operación ---
  const handleUpdateOperatingHours = async () => {
    // 1. Validaciones
    if (!timezone) {
      setSnackbar({ open: true, message: "Debe seleccionar una zona horaria válida (IANA).", severity: "error" });
      return;
    }

    for (const day of daysOfWeek) {
      const data = operatingHours[day.key];
      if (!data.closed) {
        if (!data.open || !data.close) {
           setSnackbar({ open: true, message: `Falta hora de apertura o cierre para el ${day.label}.`, severity: "error" });
           return;
        }
        if (data.open >= data.close) {
           setSnackbar({ open: true, message: `Error en ${day.label}: La hora de apertura debe ser menor a la hora de cierre.`, severity: "error" });
           return;
        }
      }
    }

    // 2. Convertir horas locales → UTC antes de enviar
    const hoursInUtc = {};
    for (const day of daysOfWeek) {
      const data = operatingHours[day.key];
      hoursInUtc[day.key] = {
        closed: data.closed,
        open:  data.closed ? data.open  : localToUtc(data.open,  timezone),
        close: data.closed ? data.close : localToUtc(data.close, timezone),
      };
    }

    // 3. Envío
    setSaving(true);
    try {
      await makeRequest(
        "PUT",
        `reservations/operating-hours?restaurant_public_id=${localStorage.getItem('restaurant_public_id') || restaurantId}`,
        { 
          operating_hours: hoursInUtc,
          timezone
        },
        "application/json",
        localStorage.getItem("token"),
        localStorage.getItem("tokenType")
      );
      setSnackbar({ open: true, message: "Horarios actualizados correctamente.", severity: "success" });
    } catch (err) {
      setSnackbar({ open: true, message: "Error al actualizar los horarios de operación.", severity: "error" });
    } finally {
      setSaving(false);
    }
  };

  const handleHourChange = (dayKey, field, value) => {
    setOperatingHours(prev => ({
      ...prev,
      [dayKey]: {
        ...prev[dayKey],
        [field]: value
      }
    }));
  };

  // --- Confirmaciones ---
  const handleConfirm = () => {
    if (!confirmData) return;
    const { type, value } = confirmData;
    if (type === "geo") updateGeolocation(value);
    if (type === "code") updateCodeStatus(value);
    setConfirmData(null);
  };

  return (
    <Fade in timeout={400}>
      <Card
        elevation={3}
        sx={{
          borderRadius: 4,
          p: 2,
          maxWidth: 650,
          margin: "auto",
          mt: 4,
          mb: 8,
          bgcolor: "rgba(255,255,255,0.05)",
          border: "1px solid rgba(255,255,255,0.08)",
          backdropFilter: "blur(8px)",
        }}
      >
        <CardHeader
          title="Configuraciones del Restaurante"
          subheader="Administra opciones críticas de operación y reservas"
          sx={{
            textAlign: "center",
            "& .MuiCardHeader-title": { color: "#fff", fontWeight: 600 },
            "& .MuiCardHeader-subheader": { color: "#b5b5b5" },
          }}
        />

        <CardContent>
          {loading ? null : (
            <>
              {/* --- Ajustes Principales --- */}
              <SettingItem
                title="Requerir Geolocalización"
                description="Solicita la ubicación de tus clientes al realizar pedidos."
                checked={requireGeolocation}
                onToggle={(value) => setConfirmData({ type: "geo", value })}
                disabled={saving}
              />

              <Divider sx={{ my: 2, borderColor: "rgba(255,255,255,0.08)" }} />

              <SettingItem
                title="Código del Restaurante"
                description={`Tu código actual es: ${codeDay}`}
                checked={isCodeEnabled}
                onToggle={(value) => setConfirmData({ type: "code", value })}
                disabled={saving}
              />

              <Divider sx={{ my: 2, borderColor: "rgba(255,255,255,0.08)" }} />

              {/* --- Modulo Contacto y Direccion --- */}
              <Box sx={{ mt: 2 }}>
                <Typography variant="subtitle1" sx={{ color: "#fff", fontWeight: 600, mb: 1 }}>
                  Número de WhatsApp & Dirección
                </Typography>
                <Typography variant="body2" sx={{ color: "#b5b5b5", mb: 2 }}>
                  Ingresa el número al que deseas que lleguen los pedidos a domicilio. (Ej: 573001234567)
                </Typography>
                <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2 }}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, flexGrow: 1 }}>
                    <TextField
                      fullWidth size="small" variant="outlined"
                      placeholder="Ej. 573001234567" label="Número de WhatsApp"
                      value={whatsappPhoneInput} onChange={(e) => setWhatsappPhoneInput(e.target.value)}
                      disabled={saving}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          color: 'white', backgroundColor: 'rgba(255,255,255,0.05)',
                          '& fieldset': { borderColor: 'rgba(255,255,255,0.2)' },
                          '&:hover fieldset': { borderColor: 'rgba(255,255,255,0.4)' },
                        },
                        '& .MuiInputLabel-root': { color: '#b5b5b5' }
                      }}
                    />
                    <TextField
                      fullWidth size="small" variant="outlined"
                      placeholder="Ej. Calle 123 # 45-67" label="Dirección del Restaurante (Opcional)"
                      value={addressInput} onChange={(e) => setAddressInput(e.target.value)}
                      disabled={saving}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          color: 'white', backgroundColor: 'rgba(255,255,255,0.05)',
                          '& fieldset': { borderColor: 'rgba(255,255,255,0.2)' },
                          '&:hover fieldset': { borderColor: 'rgba(255,255,255,0.4)' },
                        },
                        '& .MuiInputLabel-root': { color: '#b5b5b5' }
                      }}
                    />
                  </Box>
                  <Button
                    variant="contained" disabled={saving} onClick={handleUpdateWhatsapp}
                    sx={{
                      backgroundColor: '#8266ff', fontWeight: 600, textTransform: 'none',
                      '&:hover': { backgroundColor: '#6e54e0' }, minWidth: '120px', mt: 1
                    }}
                  >
                    {saving ? "Guardando..." : "Guardar"}
                  </Button>
                </Box>
              </Box>

              <Divider sx={{ my: 2, borderColor: "rgba(255,255,255,0.08)" }} />

              {/* --- Modulo Google Analytics --- */}
              <Box sx={{ mt: 2 }}>
                <Typography variant="subtitle1" sx={{ color: "#fff", fontWeight: 600, mb: 1 }}>
                  Google Analytics
                </Typography>
                <Typography variant="body2" sx={{ color: "#b5b5b5", mb: 2 }}>
                  Ingresa tu Measurement ID para registrar uso de la app. (Ej: G-XXXXXXX)
                </Typography>
                <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2 }}>
                  <TextField
                    fullWidth size="small" variant="outlined"
                    placeholder="G-XXXXXXX" label="Measurement ID"
                    value={googleAnalyticsId} onChange={(e) => setGoogleAnalyticsId(e.target.value)}
                    disabled={saving}
                    sx={{
                      flexGrow: 1,
                      '& .MuiOutlinedInput-root': {
                        color: 'white', backgroundColor: 'rgba(255,255,255,0.05)',
                        '& fieldset': { borderColor: 'rgba(255,255,255,0.2)' },
                        '&:hover fieldset': { borderColor: 'rgba(255,255,255,0.4)' },
                      },
                      '& .MuiInputLabel-root': { color: '#b5b5b5' }
                    }}
                  />
                  <Button
                    variant="contained" disabled={saving} onClick={handleUpdateAnalytics}
                    sx={{
                      backgroundColor: '#8266ff', fontWeight: 600, textTransform: 'none',
                      '&:hover': { backgroundColor: '#6e54e0' }, minWidth: '120px'
                    }}
                  >
                    {saving ? "Guardando..." : "Guardar"}
                  </Button>
                </Box>
              </Box>

              <Divider sx={{ my: 2, borderColor: "rgba(255,255,255,0.08)" }} />

              {/* --- Modulo Horarios de Operación --- */}
              <Box sx={{ mt: 2 }}>
                <Typography variant="subtitle1" sx={{ color: "#fff", fontWeight: 600, mb: 1 }}>
                  Horarios de Operación (Reservas)
                </Typography>
                <Typography variant="body2" sx={{ color: "#b5b5b5", mb: 3 }}>
                  Define en qué horario estás disponible para reservas.
                </Typography>

                <Box sx={{ mb: 3 }}>
                   <Autocomplete
                      options={Intl.supportedValuesOf('timeZone')}
                      value={timezone}
                      onChange={(e, newValue) => setTimezone(newValue)}
                      disabled={saving}
                      disableClearable
                      renderInput={(params) => (
                        <TextField {...params} label="Zona Horaria (Obligatorio)" size="small" 
                         sx={{
                           '& .MuiOutlinedInput-root': {
                             color: 'white', backgroundColor: 'rgba(255,255,255,0.05)',
                             '& fieldset': { borderColor: 'rgba(255,255,255,0.2)' },
                             '&:hover fieldset': { borderColor: 'rgba(255,255,255,0.4)' },
                           },
                           '& .MuiInputLabel-root': { color: '#b5b5b5' },
                           '& .MuiSvgIcon-root': { color: '#b5b5b5' }
                         }}
                        />
                      )}
                   />
                </Box>

                <Grid container spacing={1.5}>
                  {daysOfWeek.map((day) => {
                    const data = operatingHours[day.key];
                    return (
                      <Grid item xs={12} key={day.key}>
                        <Box sx={{ 
                          display: 'flex', alignItems: 'center', justifyContent: 'space-between', 
                          p: 1.5, borderRadius: 2, backgroundColor: 'rgba(255,255,255,0.03)',
                          border: '1px solid rgba(255,255,255,0.08)',
                          flexWrap: { xs: 'wrap', sm: 'nowrap' }, gap: 1
                        }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', minWidth: '130px' }}>
                            <Switch 
                              checked={!data.closed} 
                              onChange={(e) => handleHourChange(day.key, "closed", !e.target.checked)} 
                              disabled={saving}
                              color="primary"
                            />
                            <Typography sx={{ color: '#fff', fontWeight: 500, ml: 1 }}>{day.label}</Typography>
                          </Box>
                          
                          {!data.closed ? (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <TextField
                                type="time"
                                value={data.open}
                                onChange={(e) => handleHourChange(day.key, "open", e.target.value)}
                                disabled={saving} size="small"
                                sx={{ 
                                  width: '120px', 
                                  input: { color: '#fff', '&::-webkit-calendar-picker-indicator': { filter: 'invert(1)' } }, 
                                  '& .MuiOutlinedInput-root': {
                                    backgroundColor: 'rgba(255,255,255,0.05)',
                                    '& fieldset': { borderColor: 'rgba(255,255,255,0.2)' }
                                  } 
                                }}
                              />
                              <Typography sx={{ color: '#b5b5b5' }}>-</Typography>
                              <TextField
                                type="time"
                                value={data.close}
                                onChange={(e) => handleHourChange(day.key, "close", e.target.value)}
                                disabled={saving} size="small"
                                sx={{ 
                                  width: '120px', 
                                  input: { color: '#fff', '&::-webkit-calendar-picker-indicator': { filter: 'invert(1)' } }, 
                                  '& .MuiOutlinedInput-root': {
                                    backgroundColor: 'rgba(255,255,255,0.05)',
                                    '& fieldset': { borderColor: 'rgba(255,255,255,0.2)' }
                                  } 
                                }}
                              />
                            </Box>
                          ) : (
                             <Typography sx={{ color: '#b5b5b5', fontStyle: 'italic', pr: 2 }}>Cerrado</Typography>
                          )}
                        </Box>
                      </Grid>
                    );
                  })}
                </Grid>

                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
                   <Button
                      variant="contained" disabled={saving} onClick={handleUpdateOperatingHours}
                      sx={{
                        backgroundColor: '#8266ff', fontWeight: 600, textTransform: 'none',
                        '&:hover': { backgroundColor: '#6e54e0' }, minWidth: '150px'
                      }}
                    >
                      {saving ? "Guardando..." : "Guardar Horarios"}
                   </Button>
                </Box>
              </Box>
            </>
          )}
        </CardContent>

        <ConfirmDialog
          open={!!confirmData}
          onClose={() => setConfirmData(null)}
          onConfirm={handleConfirm}
          title="Confirmar acción"
          message={
            confirmData?.type === "geo"
              ? "¿Estás seguro de activar o desactivar la geolocalización?"
              : "Al activar o desactivar el código del restaurante, los pedidos en curso podrían verse afectados."
          }
        />

        <Snackbar
          open={snackbar.open}
          autoHideDuration={4000}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        >
          <Alert severity={snackbar.severity} variant="filled" sx={{ bgcolor: snackbar.severity === "success" ? "#8266ff" : "error" }}>
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Card>
    </Fade>
  );
};

export default RestaurantSettings;
